import { useState } from 'react';
import { Table, Button, Loader, Center, Modal, Stack, TextInput, Textarea, Select, Group, Badge, Text, ActionIcon, Tooltip } from '@mantine/core';
import { DateInput } from '@mantine/dates';
import { useForm } from '@mantine/form';
import { useDisclosure } from '@mantine/hooks';
import { IconPlus, IconUserPlus, IconMessage } from '@tabler/icons-react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { usePagedQuery } from '../hooks/usePagedQuery';
import { TableExport } from '../components/TableExport';
import { TablePagination } from '../components/TablePagination';
import { TableToolbar } from '../components/TableToolbar';
import { useDebouncedValue } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import { useTranslation } from 'react-i18next';
import api from '../services/api';
import { PageHeader } from '../components/PageHeader';
import { PhoneInput } from '../components/PhoneInput';
import { FetchOverlay } from '../components/FetchOverlay';
import { useClasses } from '../hooks/useLookups';
import { GenderInput } from '../components/QuickCreate';
import { SectionCard } from '../components/SectionCard';
import { uzDateProps } from '../lib/dateInput';

interface Admission {
  id: string;
  applicantName: string;
  parentName?: string;
  parentPhone: string;
  source?: string;
  birthDate?: string;
  note?: string;
  stage: string;
  status: string;
}

const STAGE_VALUES = ['NEW', 'CONTACTED', 'TEST', 'INTERVIEW'];
const STATUS_VALUES = ['OPEN', 'ACCEPTED', 'REJECTED', 'ENROLLED'];
const STATUS_COLOR: Record<string, string> = { OPEN: 'blue', ACCEPTED: 'teal', REJECTED: 'red', ENROLLED: 'green' };

/** Tug'ilgan sanadan yoshni hisoblaydi. */
function ageFrom(dateStr?: string): number | null {
  if (!dateStr) return null;
  const b = new Date(dateStr);
  if (isNaN(b.getTime())) return null;
  const now = new Date();
  let age = now.getFullYear() - b.getFullYear();
  const m = now.getMonth() - b.getMonth();
  if (m < 0 || (m === 0 && now.getDate() < b.getDate())) age--;
  return age >= 0 && age < 120 ? age : null;
}

export default function Admissions() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [opened, { open, close }] = useDisclosure(false);

  const STAGE_LABELS: Record<string, string> = {
    NEW: t('admissionsPage.stageNew'), CONTACTED: t('admissionsPage.stageContacted'),
    TEST: t('admissionsPage.stageTest'), INTERVIEW: t('admissionsPage.stageInterview'),
  };
  const STATUS_LABELS: Record<string, string> = {
    OPEN: t('admissionsPage.statusOpen'), ACCEPTED: t('admissionsPage.statusAccepted'),
    REJECTED: t('admissionsPage.statusRejected'), ENROLLED: t('admissionsPage.statusEnrolled'),
  };

  const [search, setSearch] = useState('');
  const [qDeb] = useDebouncedValue(search, 300);
  const [stageF, setStageF] = useState<string>('');
  const [statusF, setStatusF] = useState<string>('');
  const [sort, setSort] = useState('status:asc');
  /* Filtrlar BITTA joyda: jadval ham, XLSX eksporti ham AYNI obyektni oladi. */
  const filtrlar = {
    q: qDeb || undefined, stage: stageF || undefined, status: statusF || undefined, sort,
  };
  const { items: data, isLoading, fetching, total, pages, page, setPage, limit, setLimit} = usePagedQuery<Admission>('admissions', '/admissions', filtrlar);

  const form = useForm({
    initialValues: { applicantName: '', parentName: '', parentPhone: '', source: '', birthDate: null as Date | null, note: '' },
    validate: {
      applicantName: (v) => (!v ? t('admissionsPage.applicantRequired') : null),
      parentPhone: (v) => (!v ? t('admissionsPage.phoneRequired') : null),
    },
  });

  const createMut = useMutation({
    mutationFn: (v: typeof form.values) =>
      api.post('/admissions', { ...v, birthDate: v.birthDate ? v.birthDate.toISOString() : undefined }),
    onSuccess: () => { notifications.show({ color: 'green', message: t('admissionsPage.created') }); qc.invalidateQueries({ queryKey: ['admissions'] }); form.reset(); close(); },
  });
  const updateMut = useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: Partial<Admission> }) => api.patch(`/admissions/${id}`, patch),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admissions'] }),
  });

  // ─── Qabul → o'quvchiga aylantirish ────────────────────────────────────
  const [enrollOpened, { open: openEnroll, close: closeEnroll }] = useDisclosure(false);
  const [enrollId, setEnrollId] = useState<string | null>(null);
  const { data: classes } = useClasses();
  const enrollForm = useForm({
    initialValues: { fullName: '', classId: '', gender: '', birthDate: null as Date | null, parentName: '', parentPhone: '' },
    validate: {
      fullName: (v) => (v.trim().length < 3 ? t('admissionsPage.enrollValidateName') : null),
      classId: (v) => (!v ? t('admissionsPage.enrollValidateClass') : null),
      gender: (v) => (!v ? t('admissionsPage.enrollValidateGender') : null),
    },
  });

  const startEnroll = (a: Admission) => {
    setEnrollId(a.id);
    enrollForm.setValues({
      fullName: a.applicantName ?? '',
      classId: '',
      gender: '',
      birthDate: a.birthDate ? new Date(a.birthDate) : null,
      parentName: a.parentName ?? '',
      parentPhone: a.parentPhone ?? '',
    });
    enrollForm.clearErrors();
    openEnroll();
  };

  const enrollMut = useMutation({
    mutationFn: async (v: typeof enrollForm.values) => {
      await api.post('/students', { ...v, birthDate: v.birthDate ? v.birthDate.toISOString() : undefined });
      if (enrollId) await api.patch(`/admissions/${enrollId}`, { status: 'ENROLLED' });
    },
    onSuccess: () => {
      notifications.show({ color: 'green', message: t('admissionsPage.enrolled') });
      qc.invalidateQueries({ queryKey: ['admissions'] });
      qc.invalidateQueries({ queryKey: ['students'] });
      enrollForm.reset();
      setEnrollId(null);
      closeEnroll();
    },
    onError: (e: unknown) => {
      const msg = (e as { response?: { data?: { error?: string } } })?.response?.data?.error;
      notifications.show({ color: 'red', message: msg ?? t('common.error') });
    },
  });

  return (
    <>
      <PageHeader title={t('pages.admissions.title')} subtitle={`${t('common.total')}: ${total}`} action={<Button leftSection={<IconPlus size={18} />} onClick={open}>{t('pages.admissions.add')}</Button>} />

      <TableToolbar
        search={search}
        onSearch={(v) => { setSearch(v); setPage(1); }}
        searchPlaceholder={t('admissionsPage.searchPlaceholder')}
        sort={sort}
        onSort={(v) => { setSort(v); setPage(1); }}
        sortOptions={[
          { value: 'status:asc', label: t('admissionsPage.status') },
          { value: 'createdAt:desc', label: t('admissionsPage.sortNew') },
          { value: 'applicantName:asc', label: t('admissionsPage.sortNameAsc') },
          { value: 'stage:asc', label: t('admissionsPage.stage') },
        ]}
      >
        <Select
          placeholder={t('admissionsPage.stage')}
          data={STAGE_VALUES.map((value) => ({ value, label: STAGE_LABELS[value] }))}
          value={stageF}
          onChange={(v) => { setStageF(v ?? ''); setPage(1); }}
          clearable w={140}
        />
        <Select
          placeholder={t('admissionsPage.status')}
          data={[
            { value: 'OPEN', label: t('admissionsPage.statusOpen') },
            { value: 'ACCEPTED', label: t('admissionsPage.statusAccepted') },
            { value: 'ENROLLED', label: t('admissionsPage.filterEnrolled') },
            { value: 'REJECTED', label: t('admissionsPage.filterRejected') },
          ]}
          value={statusF}
          onChange={(v) => { setStatusF(v ?? ''); setPage(1); }}
          clearable w={130}
        />
        <TableExport<Admission>
          endpoint="/admissions"
          params={filtrlar}
          filename={t('nav.admissions')}
          ustunlar={[
            { key: 'fullName', label: t('admissionsPage.applicant') },
            { key: 'parentName', label: t('roles.PARENT') },
            { key: 'phone', label: t('fields.phone') },
            { key: 'stage', label: t('admissionsPage.stage') },
            { key: 'status', label: t('admissionsPage.status') },
          ]}
        />
      </TableToolbar>
      <SectionCard title={t('common.list')} icon={IconUserPlus} p={0} pos="relative">
        <FetchOverlay visible={fetching} />
        {isLoading ? (
          <Center h={160}><Loader /></Center>
        ) : !data.length ? (
          <Center h={140}><Text c="dimmed">{t('admissionsPage.empty')}</Text></Center>
        ) : (
          <Table.ScrollContainer minWidth={880}>
            <Table highlightOnHover verticalSpacing="sm">
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>{t('admissionsPage.applicant')}</Table.Th>
                  <Table.Th>{t('admissionsPage.age')}</Table.Th>
                  <Table.Th>{t('roles.PARENT')}</Table.Th>
                  <Table.Th>{t('fields.phone')}</Table.Th>
                  <Table.Th>{t('admissionsPage.source')}</Table.Th>
                  <Table.Th>{t('admissionsPage.stage')}</Table.Th>
                  <Table.Th>{t('admissionsPage.status')}</Table.Th>
                  <Table.Th />
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {data.map((a) => {
                  const age = ageFrom(a.birthDate);
                  return (
                    <Table.Tr key={a.id}>
                      <Table.Td>
                        <Text fw={500}>{a.applicantName}</Text>
                        {a.note ? (
                          <Group gap={4} wrap="nowrap" maw={220}>
                            <IconMessage size={13} stroke={1.8} style={{ flexShrink: 0, color: 'var(--mantine-color-dimmed)' }} />
                            <Text size="xs" c="dimmed" lineClamp={1}>{a.note}</Text>
                          </Group>
                        ) : null}
                      </Table.Td>
                      <Table.Td>{age != null ? t('admissionsPage.ageYears', { age }) : '—'}</Table.Td>
                      <Table.Td>{a.parentName ?? '—'}</Table.Td>
                      <Table.Td>{a.parentPhone}</Table.Td>
                      <Table.Td>{a.source ?? '—'}</Table.Td>
                      <Table.Td>
                        <Select
                          size="xs"
                          variant="unstyled"
                          w={120}
                          data={STAGE_VALUES.map((value) => ({ value, label: STAGE_LABELS[value] }))}
                          value={a.stage}
                          onChange={(val) => val && updateMut.mutate({ id: a.id, patch: { stage: val } })}
                          allowDeselect={false}
                        />
                      </Table.Td>
                      <Table.Td>
                        <Select
                          size="xs"
                          variant="unstyled"
                          w={120}
                          data={STATUS_VALUES.map((value) => ({ value, label: STATUS_LABELS[value] }))}
                          value={a.status}
                          onChange={(val) => val && updateMut.mutate({ id: a.id, patch: { status: val } })}
                          allowDeselect={false}
                          renderOption={({ option }) => <Badge color={STATUS_COLOR[option.value]} variant="light">{option.label}</Badge>}
                        />
                      </Table.Td>
                      <Table.Td>
                        {a.status === 'ENROLLED' ? (
                          <Badge color="green" variant="light">{t('admissionsPage.alreadyEnrolled')}</Badge>
                        ) : (
                          <Tooltip label={t('admissionsPage.enroll')}>
                            <ActionIcon variant="light" color="green" onClick={() => startEnroll(a)} aria-label={t('admissionsPage.enroll')}>
                              <IconUserPlus size={18} />
                            </ActionIcon>
                          </Tooltip>
                        )}
                      </Table.Td>
                    </Table.Tr>
                  );
                })}
              </Table.Tbody>
            </Table>
          </Table.ScrollContainer>
        )}
      </SectionCard>
      <TablePagination
        page={page}
        pages={pages}
        total={total}
        limit={limit}
        onPage={setPage}
        onLimit={setLimit}
      />

      <Modal opened={opened} onClose={close} title={t('admissionsPage.modalTitle')}>
        <form onSubmit={form.onSubmit((v) => createMut.mutate(v))}>
          <Stack>
            <TextInput label={t('admissionsPage.applicantName')} required {...form.getInputProps('applicantName')} />
            <Group grow>
              <DateInput label={t('admissionsPage.birthDate')} {...uzDateProps} {...form.getInputProps('birthDate')} />
              <TextInput label={t('admissionsPage.source')} placeholder={t('admissionsPage.sourcePlaceholder')} {...form.getInputProps('source')} />
            </Group>
            <TextInput label={t('fields.parentName')} {...form.getInputProps('parentName')} />
            <PhoneInput label={t('fields.parentPhone')} required {...form.getInputProps('parentPhone')} />
            <Textarea label={t('admissionsPage.note')} placeholder={t('admissionsPage.notePlaceholder')} minRows={3} autosize {...form.getInputProps('note')} />
            <Group justify="flex-end" mt="sm">
              <Button variant="default" onClick={close}>{t('common.cancel')}</Button>
              <Button type="submit" loading={createMut.isPending}>{t('common.save')}</Button>
            </Group>
          </Stack>
        </form>
      </Modal>

      <Modal opened={enrollOpened} onClose={closeEnroll} title={t('admissionsPage.enrollTitle')}>
        <form onSubmit={enrollForm.onSubmit((v) => enrollMut.mutate(v))}>
          <Stack>
            <Text size="sm" c="dimmed">{t('admissionsPage.enrollDesc')}</Text>
            <TextInput label={t('fields.fullName')} required {...enrollForm.getInputProps('fullName')} />
            <Select
              label={t('fields.class')}
              required
              searchable
              placeholder={t('admissionsPage.enrollValidateClass')}
              data={(classes ?? []).map((c) => ({ value: c.id, label: c.name }))}
              {...enrollForm.getInputProps('classId')}
            />
            <Group grow>
              <GenderInput {...enrollForm.getInputProps('gender')} />
              <DateInput label={t('admissionsPage.birthDate')} {...uzDateProps} {...enrollForm.getInputProps('birthDate')} />
            </Group>
            <TextInput label={t('fields.parentName')} {...enrollForm.getInputProps('parentName')} />
            <PhoneInput label={t('fields.parentPhone')} {...enrollForm.getInputProps('parentPhone')} />
            <Group justify="flex-end" mt="sm">
              <Button variant="default" onClick={closeEnroll}>{t('common.cancel')}</Button>
              <Button type="submit" loading={enrollMut.isPending}>{t('admissionsPage.enroll')}</Button>
            </Group>
          </Stack>
        </form>
      </Modal>
    </>
  );
}
